<template>
  <div
    class="vcv-editor quillWrapper"
    :class="[
      { resizable: resizable },
      'quillWrapper',
      {
        'quill--ru': currentLocale === 'ru',
        'quill--en': currentLocale === 'en',
        'quill--jp': currentLocale === 'ja',
        error: errors.has('toolbar')
      }
    ]"
  >
    <label v-if="label" class="vcv-editor__title">{{ label }}</label>
    <div
      v-if="localToolbarOptions && localToolbarOptions.modules.toolbar !== false && toolbarButtons"
      :id="'customToolbar_' + timestamp"
    >
      <select v-if="toolbarButtons['font']" class="ql-font">
        <option v-for="(item, index) in fonts" :key="index" :selected="index === 0" :value="item">
          {{ item }}
        </option>
      </select>
      <select v-if="toolbarButtons['size']" class="ql-size">
        <option value="small">{{ $tr('editor.format.small') }}</option>
        <option selected>{{ $tr('editor.format.normal') }}</option>
        <option value="large">{{ $tr('editor.format.big') }}</option>
        <option value="huge">{{ $tr('editor.format.huge') }}</option>
      </select>
      <button v-if="toolbarButtons['bold']" class="ql-bold"></button>
      <button v-if="toolbarButtons['italic']" class="ql-italic"></button>
      <button v-if="toolbarButtons['underline']" class="ql-underline"></button>
      <select v-if="toolbarButtons['align']" class="ql-align">
        <option></option>
        <option value="right"></option>
        <option value="center"></option>
        <option value="justify"></option>
      </select>
      <select v-if="toolbarButtons['color']" class="ql-color" title="Char color"></select>
      <select v-if="toolbarButtons['background']" class="ql-background" title="Bg color"></select>
      <button v-if="toolbarButtons['header']" class="ql-header" value="1"></button>
      <button v-if="toolbarButtons['header']" class="ql-header" value="2"></button>
      <button v-if="toolbarButtons['list']" class="ql-list" value="ordered"></button>
      <button v-if="toolbarButtons['list']" class="ql-list" value="bullet"></button>
      <button v-if="toolbarButtons['link']" class="ql-link"></button>
      <button v-if="toolbarButtons['clean']" class="ql-clean"></button>
      <div
        v-if="toolbarButtons['file']"
        v-tooltip.top-center="fileTooltip ? $tr(fileTooltip) : null"
        class="ql-file-wrapper"
        :class="{ 'is-disabled': disableMediaButtons }"
      >
        <input ref="file" class="ql-file" type="file" @change="uploadFile" />
        <div class="ql-upload-btn">
          <img src="/static/images/elements/upload-icon.svg" />
        </div>
      </div>
      <template v-if="!customMedia">
        <button
          v-if="toolbarButtons['image']"
          v-tooltip.top-center="$tr('editor.uploadImageTooltip')"
          class="ql-image"
        ></button>
        <button v-if="toolbarButtons['video']" class="ql-video"></button>
      </template>
      <span v-if="toolbarButtons['vars'] && vars.length > 0" class="ql-vars">
        <varsinsert :list="vars" @insert="insert_var" />
      </span>
      <slot name="toolbar"></slot>
    </div>
    <vue-editor
      v-if="localToolbarOptions && localToolbarOptions.modules.toolbar"
      v-bind="{ ...$props, ...$attrs }"
      :id="'id-' + Date.now()"
      :ref="refProp"
      v-model="val"
      :resizable="resizable"
      :class="{ error: error.length > 0 }"
      use-custom-image-handler
      :editor-options="localToolbarOptions"
      :placeholder="placeholder"
      :style="{ borderColor: errors.has('description') ? 'red' : '' }"
      @image-added="handleImageAdded"
      @video-added="handleVideoAdded"
      @text-change="handleTextChange"
      @selection-change="selection_change"
      @focus="remove_placeholder"
      @blur="set_placeholder"
    />
    <vue-editor
      v-else
      v-bind="{ ...$props, ...$attrs }"
      :id="'id-' + Date.now()"
      :ref="refProp"
      v-model="val"
      :resizable="resizable"
      :editor-options="{
        modules: {
          toolbar: false
        }
      }"
      :placeholder="placeholder"
      @text-change="handleTextChange"
      @selection-change="selection_change"
      @focus="remove_placeholder"
      @blur="set_placeholder"
    />
    <div v-if="loading" class="preload-img">
      <div class="preload-img__load">{{ $tr('veditor.uploadImage') }}</div>
      <div class="preload-img__cancel" @click="cancelLoaderImg">{{ $tr('veditor.cancelImg') }}</div>
    </div>
    <div v-if="!!error" class="message">{{ error }}</div>
    <label v-if="errorsAtLogo.show" class="vcv-editor__helper">{{ errorsAtLogo.message }}</label>
  </div>
</template>

<script>
import { VueEditor, Quill } from 'vue2-editor';
import { merge as _merge } from 'lodash';
var Font = Quill.import('formats/font');
const FONTS = ['roboto', 'serif', 'monospace', 'helvetica', 'arial'];
// import QuillBetterTable from 'quill-better-table';

const IMAGE_MAX_SIZE_BYTES = 15 * 1024 * 1024;
const DEFAULT_TOOLBAR_BUTTONS = {
  font: false,
  size: false,
  bold: false,
  italic: false,
  underline: false,
  align: false,
  color: false,
  background: false,
  header: false,
  list: false,
  link: false,
  image: false,
  video: false,
  vars: false,
  file: false,
  clean: false
};
Font.whitelist = FONTS;
Quill.register(Font, true);
// Quill.register('modules/better-table', QuillBetterTable);

export default {
  name: 'VcvEditor',

  components: {
    VueEditor
  },

  props: {
    label: {
      type: [String],
      default: ''
    },

    fileTooltip: {
      type: [String],
      default: ''
    },

    refProp: {
      type: [String, Number],
      default: 'ref-' + Date.now()
    },

    value: {
      type: [String],
      default: ''
    },

    vars: {
      type: [Array],
      default: () => []
    },

    placeholder: {
      type: [String, Number],
      default: ''
    },

    disableMediaButtons: {
      type: [Boolean],
      default: false
    },

    resizable: {
      type: [Boolean],
      default: false
    },

    error: {
      type: [String],
      default: ''
    },

    toolbarOptions: {
      type: Object,
      default: () => ({})
    },

    toolbarButtons: {
      type: Object,
      default: () => DEFAULT_TOOLBAR_BUTTONS
    },

    customMedia: {
      type: [Boolean],
      default: false
    }
  },

  data() {
    return {
      timestamp: '0',
      fonts: FONTS,
      body: null,
      file: null,
      loading: false,
      errorsAtLogo: {
        show: false,
        message: ''
      }
    };
  },

  computed: {
    quill() {
      return this.$refs[this.refProp].quill;
    },

    currentLocale() {
      return this.$store.state.locale.value;
    },

    localToolbarOptions() {
      let options = {
        modules: {
          // table: false, // disable table module
          // 'better-table': {
          //   operationMenu: {
          //     items: {
          //       unmergeCells: {
          //         text: 'Another unmerge cells name'
          //       }
          //     }
          //   }
          // },

          // keyboard: {
          //   bindings: QuillBetterTable.keyboardBindings
          // },

          toolbar: {
            container: `#customToolbar_${this.timestamp}`,
            handlers: {
              customSize: val => {
                this.quill.insertText(this.quill.getSelection(true).index, ` {{${val}}} `);
              },

              link: value => {
                if (value) {
                  var href = prompt('Enter the URL');

                  if (href && href.indexOf('http') < 0) {
                    href = `http://${href}`;
                  }
                  this.quill.format('link', href);
                } else {
                  this.quill.format('link', false);
                }
              },

              video: value => {
                if (value) {
                  var videoLink = prompt('Enter Youtube video link');
                  var youtubeRegEx = /www\.youtu/;
                  var embedLink = this.getYoutubeEmbedLink(videoLink);

                  if (videoLink && !embedLink.match(youtubeRegEx)) {
                    this.quill.format('video', false);
                    this.$noty.error(this.$tr('landing.video.editor.youtubeError'));
                  } else if (videoLink) {
                    this.quill.format('video', this.getYoutubeEmbedLink(videoLink));
                  }
                } else {
                  this.quill.format('video', false);
                }
              }
            }
          }
        }
      };
      if (this.toolbarOptions && this.toolbarOptions.modules && this.toolbarOptions.modules.toolbar) {
        if (this.toolbarOptions.modules.toolbar === false) {
          options = {
            modules: {
              toolbar: false
            }
          };
        } else {
          options = _merge(options, this.toolbarOptions);
        }
      }
      return options;
    },

    companyImage() {
      return this.$store.getters['services/companyImage'];
    },

    val: {
      get() {
        if (this.body === null) {
          return this.value;
        }

        return this.body;
      },

      set(val) {
        this.body = val;
        this.$emit('input', val);
      }
    }
  },

  beforeMount() {
    const date = new Date();
    this.timestamp = date.getTime();
  },

  methods: {
    uploadFile() {
      const files = this.$refs.file.files;
      const file = files[0];

      if (!file) {
        return;
      }
      // const size = file.size ? file.size / 1024 / 1024 : 0;

      if (file) {
        this.$store
          .dispatch('services/saveCompanyImage', {
            imageUrl: file
          })
          .then(data => {
            const href = data.url;
            this.quill.clipboard.dangerouslyPasteHTML(
              this.quill.getSelection(true).index,
              `<a href="${href}">${this.$tr('editor.uploadFile')}</a>`
            );

            this.$refs.file.value = '';
          })
          .catch(err => {
            if (err && err.response && err.response.data && err.response.data.meta) {
              this.$noty.error(err.response.data.meta.error.message);
            }
            this.$refs.file.value = '';
          });
      } else {
        this.file = null;
      }
    },

    insert_var(id) {
      let quill = this.quill;
      quill.insertText(quill.getSelection(true).index, ` {{${id}}} `);
    },

    remove_placeholder() {
      this.$refs[this.refProp].$refs.quillContainer.querySelector('.ql-editor').setAttribute('data-placeholder', '');
    },

    set_placeholder() {
      this.$refs[this.refProp].$refs.quillContainer
        .querySelector('.ql-editor')
        .setAttribute('data-placeholder', this.placeholder);
    },

    handleImageAdded(file, Editor, cursorLocation, resetUploader) {
      if (!file.type.includes('image/')) {
        this.errorsAtLogo.message = this.$tr('profile.profileForm.photoSelector.format');
        this.errorsAtLogo.show = true;
        return false;
      }

      if (file.size / IMAGE_MAX_SIZE_BYTES >= 1) {
        this.errorsAtLogo.message = this.$tr('profile.profileForm.photoSelector.tooLarge');
        this.errorsAtLogo.show = true;
        return false;
      }

      this.loading = true;
      this.errorsAtLogo.message = '';
      this.errorsAtLogo.show = false;

      this.$store
        .dispatch('services/saveCompanyImage', {
          imageUrl: file
        })
        .then(() => {
          this.loading = false;
          Editor.insertEmbed(cursorLocation, 'image', this.companyImage.url);
          resetUploader();
        });
    },

    handleVideoAdded(val) {
      console.log('handleVideoAdded: ', val);
    },

    handleTextChange(val) {
      this.$emit('text-change', val);
    },

    selection_change(val) {
      this.$emit('selection-change', val);
    },

    cancelLoaderImg() {
      this.loading = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.vcv-editor {
  position: relative;
  .ql-vars {
    display: inline-block;
    vertical-align: middle;
    margin: 0 6px;
    color: #7f8b99;
    ::v-deep .vars_insert .value .icon {
      color: #7f8b99;
    }
    &:hover {
      ::v-deep .vars_insert .value .icon {
        color: $green;
      }
    }
  }
  &__title {
    font-size: 15px;
    display: block;
    margin-bottom: 10px;
  }

  &__helper {
    display: block;
    color: $red-color;
    margin-bottom: 20px;
    font-weight: normal;
    padding-top: 5px;
  }
  .message {
    color: #f65046;
    // // font-family: Roboto;
    font-style: normal;
    font-weight: normal;
    line-height: 19px;
    font-size: 13px;
    letter-spacing: 0.005em;
    margin-top: 5px;
    margin-left: 10px;
  }
  .error {
    outline: 1px solid #f65046;
  }

  .preload-img {
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.9);
    position: absolute;
    left: 0;
    bottom: 0;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;

    &__load {
      position: relative;
      padding-left: 26px;
      margin-right: 8px;

      &:before {
        content: '';
        display: block;
        width: 17px;
        height: 17px;
        background: url('/static/images/elements/vacancy/Ellipse.png') center no-repeat;
        position: absolute;
        left: 0;
        top: 0;
        animation: 1s linear 0s normal none infinite running rot;
      }
    }

    &__cancel {
      color: #607183;
      cursor: pointer;
    }
  }

  &.resizable#vcv-editor-vacancy {
    ::v-deep {
      .ql-container {
        height: auto;
        min-height: auto;

        .ql-editor {
          height: 244px;
          min-height: 244px;
          resize: vertical;
        }
      }
    }
  }
}

@keyframes rot {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
